import React, { useRef, useState } from "react";
import Markdown from "markdown-to-jsx";
import { overrides } from "@shared/components/Assistant/MarkdownConfig";
import { useProjectChat } from "@services/ProjectChatContext";
import { useUser } from "@services/UserContext";
import { getPredefinedQuestions } from "@shared/components/Assistant/utils/chatQuestions";
import { useFeedback } from "@shared/components/Feedback/utils/useFeedback";
import ThumbButtons from "@shared/components/Feedback/ThumbButtons";
import FeedbackSubmission from "@shared/components/Feedback/FeedbackSubmission";
import Loader from "@shared/components/Loader";
import { Tooltip } from "flowbite-react";

import { useLoadingStream } from "@pages/Assistant/utils/useLoadingStream";
import {
  ThinkingPanel,
  StepsPanel,
} from "@pages/Assistant/components/renderLoadingItems";

import { CitationViewItem } from "@pages/Assistant/utils/citations";
import ImageViewer from "@pages/Assistant/components/ImageViewer";
import { useRenderHelper } from "../../Assistant/utils/useRenderHelper";
import useReportAssistantSocket from "../utils/useReportAssistantSocket";

type InlineBadge = {
  n: number;
  project_id?: string | null;
  file_id?: string;
  original_file?: string;
  page_number?: number;
  sheet_name?: string;
  start_row?: number;
  hoverText: string;
  kind?: "document" | "tabular" | "web";
  url?: string;
  title?: string;
};

interface ChatProps {
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  openReportEditingSocket: (taskId: string) => void;
  isReportEditing: boolean;
  isReportHeaderVisible: boolean;
  isEditing: boolean;
  setShowDocxModal?: (show: boolean) => void;
  setDocxContent?: (content: string) => void;
  setShowExcelModal?: (show: boolean) => void;
  setExcelData?: (data: any[][]) => void;
}

const ReportAssistant: React.FC<ChatProps> = ({
  isCollapsed = false,
  setIsCollapsed,
  openReportEditingSocket,
  isReportEditing,
  isReportHeaderVisible,
  isEditing,
  setShowDocxModal = () => false,
  setDocxContent = () => false,
  setShowExcelModal = () => false,
  setExcelData = () => false,
}) => {
  // ----- Refs & state -----
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);

  const [question, setQuestion] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const [predefinedQuestions, setPredefinedQuestions] = useState<string[]>(
    getPredefinedQuestions("/report/")
  );
  const [isFirstInteraction, setIsFirstInteraction] = useState(true);

  const [showLoading, setShowLoading] = useState<boolean>(false);

  const citationsV2Ref = useRef<CitationViewItem[]>([]);
  const inlineBadgesRef = useRef<InlineBadge[]>([]);
  const nextBadgeNumRef = useRef<number>(1);

  const imageRef = useRef<any[]>([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState<string | null>(null);
  const [modalImageAlt, setModalImageAlt] = useState<string>("");

  const [isChatLoading, setIsChatLoading] = useState(false);
  const thinkingReceived = useRef<boolean>(false);

  const {
    thinking: thinkingItem,
    steps: stepItem,
    ingest,
    clear: clearLoading,
  } = useLoadingStream();

  const { chatSessionId, setChatSessionId, user } = useUser();

  // project/report chat context
  const {
    projectId,
    projectName,
    addMessageToChat,
    chatLog,
    setChatLog,
    clearChat,
    reportId,
    reportName,
    setSelectedFiles,
    selectedFiles,
  } = useProjectChat();

  // conversation references
  const [conversationId, setConversationId] = useState<string>("");
  const conversationIdRef = useRef<string | null>(null);

  // ----- Feedback hook -----
  const {
    feedbackText,
    setFeedbackText,
    feedbackOption,
    setFeedbackOption,
    feedbackState,
    setFeedbackState,
    feedbackSubmittedFor,
    selectedThumbs,
    feedbackHiddenFor,
    feedbackUpdating,
    handleThumbClick,
    handleFeedbackSubmit,
    scrollToBottom,
    feedbackForIndex,
    chatLogRef,
  } = useFeedback(chatSessionId || "");

  // ----- Render helper (inline images + citations) -----
  const { renderBotMessage } = useRenderHelper(
    overrides,
    inlineBadgesRef,
    setShowDocxModal,
    setDocxContent,
    setExcelData,
    setShowExcelModal,
    addMessageToChat,
    setChatLog,
    imageRef,
    setModalImageSrc,
    setModalImageAlt,
    setShowImageModal
  );

  // ----- Core WS Connect -----

  const { handleSubmit, tryAgain, sendQuestion } = useReportAssistantSocket({
    reportId,
    projectId,
    projectName,
    chatSessionId,
    setChatSessionId,
    conversationIdRef,
    setConversationId,
    chatLog,
    setChatLog,
    question,
    setQuestion,
    setIsFirstInteraction,
    setIsChatLoading,
    socket,
    setSocket,
    addMessageToChat,
    renderBotMessage,
    setShowLoading,
    clearLoading,
    setError,
    scrollToBottom,
    openReportEditingSocket,

    citationsV2Ref,
    inlineBadgesRef,
    nextBadgeNumRef,
    imageRef,
    thinkingReceived,
    ingest,
  });

  // ----- Effects -----

  if (isEditing) return null;

  return (
    <div
      ref={chatContainerRef}
      className={`flex flex-col ${
        isCollapsed ? "w-[4.5rem]" : "w-[45rem]"
      } bg-white transition-all duration-250 ease-in-out origin-bottom-right overflow-hidden ${
        isReportHeaderVisible ? "max-h-[calc(100vh-78px)]" : "max-h-[100vh]"
      } border-l-2 border-gray-100`}
    >
      {isCollapsed ? (
        <div className=" text-gray-900 font-semibold h-[4.5rem] p-4 border-b-2 border-gray-100 flex items-center justify-end w-full mx-auto">
          <Tooltip content="View Assistant" placement="left" arrow={false}>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="bg-white mr-[0.6rem]"
            >
              <img src="/right_panel_open.svg" className="w-5 h-5" alt="" />
            </button>
          </Tooltip>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className=" text-gray-900 font-semibold h-[4.5rem] p-4 border-b-2 border-gray-100 flex items-center justify-between">
            <p>Report Assistant</p>
            <Tooltip content="Close" placement="left" arrow={false}>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="bg-white mr-[0.6rem]"
              >
                <img src="/right_panel_close.svg" className="w-5 h-5" alt="" />
              </button>
            </Tooltip>
          </div>

          {/* Chat */}
          <div
            className="flex-1 p-4 space-y-4 overflow-y-auto"
            id="chatlogDiv"
            ref={chatLogRef}
          >
            <div className="flex">
              <div
                className="bg-[#F2F4F7] border border-gray-300 text-gray-800 p-3 rounded-md whitespace-pre-wrap break-words text-sm 2xl:text-base"
                style={{ maxWidth: "75%" }}
              >
                Hi, I am your Report Assistant. I can help you by answering
                questions about the report, editing sections, or adding new
                graphs/tables.
                <br />
                <br />
                e.g. “Generate a table from the data in section 3.4”
              </div>
            </div>

            {/* Messages */}
            {chatLog.map((msg, index) => {
              return (
                <div key={index}>
                  <div
                    className={`p-3 rounded-lg whitespace-pre-wrap break-words text-sm 2xl:text-base ${
                      msg.sender === "user"
                        ? "bg-[#0056C8] text-white ml-auto"
                        : "bg-[#F2F4F7] border border-gray-300  text-gray-800 mr-auto"
                    } ${msg.message === "" ? "hidden" : ""}`}
                    style={{ maxWidth: "75%" }}
                  >
                    {/* IMPORTANT: do not pre-render a top gallery for new chats;
                        images are inlined via tokens during stream. */}
                    {msg.message !== "" && (
                      <>
                        {React.isValidElement(msg.message) ||
                        Array.isArray(msg.message) ? (
                          <>{msg.message}</>
                        ) : typeof msg.message === "string" ? (
                          <Markdown options={{ overrides }}>
                            {String(msg.message)}
                          </Markdown>
                        ) : (
                          <div className="text-red-500">
                            ⚠️ Invalid message format
                          </div>
                        )}
                      </>
                    )}

                    {/* Thumbs for bot messages */}
                    {!isChatLoading && msg.sender === "bot" && (
                      <ThumbButtons
                        index={index}
                        selectedThumbs={selectedThumbs}
                        feedbackSubmittedFor={feedbackSubmittedFor}
                        handleThumbClick={handleThumbClick}
                        feedbackUpdating={feedbackUpdating}
                      />
                    )}
                  </div>

                  {!isChatLoading &&
                    feedbackForIndex === index &&
                    !feedbackHiddenFor[index] && (
                      <div className="w-[75%]">
                        <FeedbackSubmission
                          index={index}
                          feedbackSubmittedFor={feedbackSubmittedFor}
                          feedbackState={feedbackState}
                          feedbackOption={feedbackOption}
                          feedbackText={feedbackText}
                          feedbackUpdating={feedbackUpdating}
                          setFeedbackText={setFeedbackText}
                          setFeedbackOption={setFeedbackOption}
                          setFeedbackState={setFeedbackState}
                          handleFeedbackSubmit={handleFeedbackSubmit}
                        />
                      </div>
                    )}
                </div>
              );
            })}

            {/* Separate loading panels */}
            {isChatLoading && showLoading && (
              <div className="space-y-3 max-w-[75%]">
                {thinkingItem && <ThinkingPanel item={thinkingItem} />}
                {stepItem && <StepsPanel item={stepItem} />}
              </div>
            )}
          </div>

          {error && (
            <div className=" text-center py-4 max-w-96 mx-auto">
              <div className=" text-red-500 bg-red-100 py-2 px-4 border-red-500 border rounded-lg">
                <p>{error}</p>
              </div>
              <button
                onClick={tryAgain}
                className="cursor-pointer my-4 text-sm bg-gray-100 text-blue-500 border-rounded px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Predefined Questions */}
          <div className="gap px-4 background-transparent">
            {isFirstInteraction &&
              chatLog.length === 0 &&
              predefinedQuestions.map((q, index) => (
                <div className="flex items-end justify-end my-2 " key={index}>
                  <button
                    onClick={() => {
                      sendQuestion(q);
                    }}
                    className="bg-[#edf2fc] text-[#2563eb] px-3 py-2 rounded-lg text-sm hover:bg-[#cedefc] transition border-[#2563eb] border-2"
                  >
                    {q}
                  </button>
                </div>
              ))}
          </div>

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            className="flex items-center p-3 border-t bg-[#F2F4F7] py-6"
          >
            <div className="flex items-center w-full space-x-4 bg-[#FFFFFE] rounded-md py-1 pr-3 border border-[#E4E7EC]">
              <input
                type="text"
                className="flex-1 py-2 px-2.5 text-xs 2xl:text-sm bg-transparent focus:outline-none disabled:cursor-not-allowed"
                placeholder="Ask to modify report..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                disabled={isChatLoading || isReportEditing}
              />
              {isChatLoading || isReportEditing ? (
                <Loader color="#2162EB" />
              ) : (
                <button
                  type="submit"
                  className="ml-2 bg-[#0056C8] text-white p-1.5 rounded-md text-sm disabled:bg-blue-400 disabled:cursor-not-allowed"
                  disabled={isChatLoading || isReportEditing}
                >
                  <img src="/submit.svg" className="w-3.5 h-3.5" alt="" />
                </button>
              )}
            </div>
          </form>
        </>
      )}

      {/* Image modal */}
      <ImageViewer
        showImageModal={showImageModal}
        setShowImageModal={setShowImageModal}
        modalImageSrc={modalImageSrc || ""}
        modalImageAlt={modalImageAlt || ""}
      />
    </div>
  );
};

export default ReportAssistant;
